import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, UntypedFormArray } from '@angular/forms';
import { TypedDictionary, WindowService } from '@core/index';
import { MultiCityRowComponent } from '@dynamic-components/booker-v2/shared/multi-city-row/multi-city-row.component';
import { BookerResponse, BookerType } from '@shared/booker';
import { BookerFacade } from '@store/booker/booker.facade';
import { PairedRecentAirportSearches } from '@store/booker/types/booker-selector.types';
import { applyFilters } from '@store/origins/api';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport, Region } from '@store/origins/types';
import { merge, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { BookerMultiCityStore } from './store/booker-multi-city.store';

@Component({
  selector: 'dot-booker-air-multi-city',
  templateUrl: './booker-air-multi-city.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookerAirMultiCityComponent implements OnInit, OnDestroy {
  @Input() content: BookerResponse;
  @Input() regions: TypedDictionary<Region>;
  @Input() separatePartnerCities: boolean;
  @Input() pax?: AbstractControl;
  @Input() showValidationErrors = false;
  @Input() recentAirports: PairedRecentAirportSearches[] = [];
  @Input() isInternationalInterline = false;
  formWrapper: HTMLElement;
  bookerType = BookerType;
  isMobile: boolean;

  @Output() firstRowUpdated = new EventEmitter<{
    origin: Airport;
    destination: Airport;
  }>();

  @ViewChildren(MultiCityRowComponent)
  multiCityRowComponents: QueryList<MultiCityRowComponent>;
  isMaxFlights$: Observable<boolean> = this.bookerMultiCityStore.isMaxFlights$;
  flights: UntypedFormArray;
  allFilteredAirports$ = this.originsFacade.countriesWithAirports.pipe(
    filter(countries => !!countries?.length),
    map(countries => applyFilters(countries, false, false, [])),
  );

  private subscriptions: Subscription = new Subscription();

  constructor(
    public readonly bookerMultiCityStore: BookerMultiCityStore,
    private originsFacade: OriginsFacade,
    private bookerFacade: BookerFacade,
    public element: ElementRef,
    private windowService: WindowService,
  ) {
    this.formWrapper = this.element.nativeElement;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.bookerMultiCityStore.flights$
        .pipe(
          switchMap(flights =>
            merge(
              ...flights.controls.map((rowFormGroup, rowIndex) =>
                rowFormGroup.get('fromAirport').valueChanges.pipe(
                  filter(updatedFromAirportValue => !!updatedFromAirportValue),
                  map(updatedFromAirportValue => ({
                    updatedFromAirportValue,
                    rowIndex,
                  })),
                ),
              ),
            ),
          ),
          distinctUntilChanged(
            (prev, current) =>
              prev.rowIndex === current.rowIndex &&
              prev.updatedFromAirportValue?.code ===
                current.updatedFromAirportValue?.code,
          ),
        )
        .subscribe(changeData => {
          this.originsFacade.requestDestinationAirports(
            changeData.updatedFromAirportValue.code,
            `MULTI_ROW_${changeData.rowIndex}`,
          );
        }),
    );
    this.subscriptions.add(
      this.windowService.isMobile.subscribe(mobile => {
        this.isMobile = mobile;
      }),
    );
  }

  addNewRow() {
    this.bookerMultiCityStore.addFlight();
    if (!this.isMobile) {
      // Slight delay to wait for new row with new inputs added
      requestAnimationFrame(() => {
        const allInputs = this.formWrapper.querySelectorAll('input');
        const newRowFromField = allInputs[allInputs.length - 3];
        newRowFromField.focus();
      });
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  handleAirportChange(
    $event: { origin: Airport; destination: Airport },
    index: number,
  ): void {
    if (index === 0) {
      this.firstRowUpdated.emit($event);
    }
  }
}
